import { Navigate } from 'react-router-dom';
import { Ellie } from '../components/Ellie';
import { useFeatureFlag } from '../services/featureFlag.service';
import { FeatureFlag } from '../services/featureFlag.service';
import FeatureFlagService from '../services/featureFlag.service';
import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { EllieHeader } from '../components/EllieHeader';
import { useIsMobileApp } from '../hooks/use-mobile';
import MobileEllie from './mobile/MobileEllie';

export default function ElliePage({
  embedThreadId,
  showHistory: embedShowHistory = false,
  centerInputOnNewChat: embedCenterInput,
  placeholder: embedPlaceholder,
  welcomeMessage: embedWelcomeMessage,
}: {
  embedThreadId?: string;
  showHistory?: boolean;
  centerInputOnNewChat?: boolean;
  placeholder?: string;
  welcomeMessage?: string;
} = {}) {
  const isAiAssistantEnabled = useFeatureFlag(FeatureFlag.AI_ASSISTANT, false);
  const useCasesEnabled = useFeatureFlag(FeatureFlag.ALLOW_USECASE_MANAGEMENT, false);
  const [flagsReady, setFlagsReady] = useState(() => FeatureFlagService.getInstance().isReady);
  const isMobileApp = useIsMobileApp();
  const isEmbedded =
    typeof window !== 'undefined' && window.location.pathname === '/embed';

  useEffect(() => {
    if (flagsReady) return;
    FeatureFlagService.getInstance().waitForInitialization().then(() => setFlagsReady(true));
  }, [flagsReady]);

  if (!flagsReady) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Flag off: Ellie isn't part of this tenant's plan. In-app, send them home —
  // the nav item and the home page's ask box are gone too, so a bookmarked
  // /ellie is the only way in and it shouldn't sit on a spinner forever. An
  // embed has no app to go home to, so it just renders nothing.
  if (!isAiAssistantEnabled) {
    return isEmbedded ? null : <Navigate to="/" replace />;
  }

  const agentType = useCasesEnabled ? 'ellie' : 'analyst';

  // Phones get the history rail as a sheet instead of a permanent column; see
  // MobileEllie. Embeds are excluded by useIsMobileApp (they're framed).
  if (isMobileApp) {
    return <MobileEllie agentType={agentType} />;
  }

  return (
    <div className="h-screen w-full flex flex-col">
      {/* Embedded chat is white-labeled: no Ellie identity header, no advanced controls */}
      {!isEmbedded && <EllieHeader />}
      <div className="flex-1 min-h-0 overflow-hidden">
        {isEmbedded ? (
          // With history, the chat + sidebar use the full width; without it,
          // the lone conversation stays centered at 3/4 width.
          <div className={embedShowHistory ? 'h-full w-full' : 'h-full w-3/4 mx-auto'}>
            <Ellie showHistory={embedShowHistory} agentType={agentType} conversationId={embedThreadId} embedded centerInputOnNewChat={embedCenterInput} placeholder={embedPlaceholder} welcomeMessage={embedWelcomeMessage} />
          </div>
        ) : (
          <Ellie showHistory={true} agentType={agentType} />
        )}
      </div>
    </div>
  );
}
